import { Card, Space, Tooltip, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { semantic } from '@/theme/tokens';

const { Text } = Typography;

export interface MetricCardProps {
  /** Top-label ("Contract value", "AR > 90d"). */
  label: string;
  /**
   * Optional tooltip rendered next to the label as an info icon. Use this to
   * disclose that a value is derived/computed at view time (e.g., weighted
   * pipeline, project margin) per FR-086 / FR-203.
   */
  labelTooltip?: string;
  /** Pre-formatted primary value. Pass via `format.currency` / `format.number`. */
  value: string;
  /** Optional secondary value caption (e.g., "SAR", period, unit). */
  unit?: string;
  /** Delta vs prior period. Positive = good by default; pass `inverted` for metrics where "down is good" (e.g., overdue). */
  delta?: {
    /** Pre-formatted delta label (e.g., "+12.4%", "−3 days"). */
    label: string;
    direction: 'up' | 'down' | 'flat';
    /** When true, `down` is treated as good (e.g., overdue AR decreasing). */
    inverted?: boolean;
  };
  /** Optional chart slot — caller provides the sparkline. */
  spark?: ReactNode;
  /** Emphasize with a colored left/inline-start border. */
  accentColor?: string;
  /** Loading stub. */
  loading?: boolean;
  /** Compact layout for role dashboards that pack many cards. */
  dense?: boolean;
}

/**
 * KPI card used by every role dashboard + detail-page KPI strips.
 *
 * RTL note:
 *  - The delta icon ALWAYS reflects financial direction (up=increase,
 *    down=decrease). It does not mirror in RTL. Arabic users read numbers
 *    left-to-right, and swapping the arrow in RTL would misrepresent the
 *    data. Decided in design-system spec.
 *  - The accent strip uses `borderInlineStart` so it renders on the right
 *    edge in RTL automatically.
 *  - Spacing uses logical props everywhere.
 */
export default function MetricCard({
  label,
  labelTooltip,
  value,
  unit,
  delta,
  spark,
  accentColor,
  loading,
  dense,
}: MetricCardProps) {
  const deltaColor = resolveDeltaColor(delta);
  const DeltaIcon = delta?.direction === 'up'
    ? ArrowUpOutlined
    : delta?.direction === 'down'
      ? ArrowDownOutlined
      : null;

  return (
    <Card
      size={dense ? 'small' : 'default'}
      loading={loading ?? false}
      variant="outlined"
      style={{
        ...(accentColor && {
          borderInlineStartWidth: 3,
          borderInlineStartColor: accentColor,
          borderInlineStartStyle: 'solid',
        }),
      }}
      styles={{ body: { padding: dense ? 12 : 16 } }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
          {labelTooltip && (
            <Tooltip title={labelTooltip}>
              <InfoCircleOutlined
                style={{ marginInlineStart: 4, color: 'rgba(0,0,0,0.45)' }}
              />
            </Tooltip>
          )}
        </Text>
        <Space align="baseline" size={6}>
          <span style={{ fontSize: dense ? 20 : 24, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}>
            {value}
          </span>
          {unit && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {unit}
            </Text>
          )}
        </Space>
        {delta && (
          <Space size={4} align="center">
            {DeltaIcon && <DeltaIcon style={{ color: deltaColor, fontSize: 12 }} />}
            <span style={{ color: deltaColor, fontSize: 12, fontWeight: 500 }}>{delta.label}</span>
          </Space>
        )}
        {spark && <div style={{ marginTop: 4 }}>{spark}</div>}
      </Space>
    </Card>
  );
}

function resolveDeltaColor(delta: MetricCardProps['delta']): string {
  if (!delta) return semantic.status.neutral;
  if (delta.direction === 'flat') return semantic.status.neutral;
  const goodUp = !delta.inverted;
  const isGood = (delta.direction === 'up' && goodUp) || (delta.direction === 'down' && !goodUp);
  return isGood ? semantic.status.success : semantic.status.error;
}
